/**
 * ForecastPage — the exec-facing Combined Revenue Forecast tab.
 *
 * REUSES the Phase 4/5 design system verbatim — AppShell chrome, FreshnessPill,
 * MainTileSection header pattern, MainTableTile anatomy, the UX-07 state
 * machine, @theme tokens. Introduces NO new visual language.
 *
 * Layout:
 *   - AppShell brandSuffix="Forecast", FreshnessPill, crossLink to /main
 *   - a CQ/NQ/FY period segmented control (?period deep-link + cache key)
 *   - ONE revenue section (ARR)
 *
 * PHASE 05.3 D-10 — the Implementation Services (T&M) section is gone from this
 * page, from the reader and from the shared contract. The second section card,
 * its weight legend, its last-pull freshness badge and the combined ARR-only
 * footnote went with it: the response no longer carries the fields they read.
 * Managed Services did NOT go with it (D-12) — it is a Salesforce-read ARR
 * stream and appears as an ARR sub-group with no budget of its own (D-05).
 *
 * The SF-OAuth exec read gate (05.1 D-19) is server-side; a 401 bounces via
 * apiFetch. A 503 CACHE_NOT_READY surfaces as the amber cache-warming banner
 * (threaded into the section card as isError/error/refetch).
 */
import {
  currentForecastQuarterTab,
  forecastQuarterWindow,
  nextForecastQuarterTab,
} from '@saledash/shared/forecast/quarters';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { AppShell } from '../components/AppShell.js';
import { ForecastAccessPanel } from '../components/forecast/ForecastAccessPanel.js';
import { ForecastEquationStrip } from '../components/forecast/ForecastEquationStrip.js';
import { ForecastPeriodSelector } from '../components/forecast/ForecastPeriodSelector.js';
import { ForecastScratchpad } from '../components/forecast/ForecastScratchpad.js';
import { ForecastSectionCard } from '../components/forecast/ForecastSectionCard.js';
import { ForecastWorkbookArchive } from '../components/forecast/ForecastWorkbookArchive.js';
import { ForecastYtdBox } from '../components/forecast/ForecastYtdBox.js';
import { FreshnessPill } from '../components/FreshnessPill.js';
import { resolveLoginUrl, UnauthenticatedError } from '../lib/api-client.js';
import {
  clampUpsidePct,
  DEFAULT_UPSIDE_PCT,
  mergeYtdQuarters,
  streams,
} from '../lib/forecast-weighting.js';
import { downloadWorkbook } from '../lib/forecast-workbook.js';
import { useForecastQuarters } from '../queries/useForecast.js';
import { useForecastWorkbooks } from '../queries/useForecastWorkbooks.js';

import type { ForecastWorkbookPeriod } from '../lib/forecast-workbook.js';
import type {
  ForecastPeriod,
  ForecastResponse,
  FreshnessPill as FreshnessPillData,
} from '@saledash/shared';

const PAGE_TITLE = 'Combined Revenue Forecast · Adeptia';

/**
 * THE PRODUCT WEIGHT LEGEND IS GONE — PLAN 08 TASK 2 SETTLED IT BY DELETION.
 *
 * Plan 07 left it here stating a fixed Best Case percentage, on the explicit
 * condition that this plan resolved it. Two outcomes were open: delete it, or
 * rebuild it by interpolation from the live Upside %. Deletion is the one taken.
 *
 * WHY: the equation strip's per-stream won / commit / upside breakdown already
 * shows how each figure is composed, and the toggle displays its own value. A
 * legend restating both narrates rather than labels, which is what D-13d
 * removes. Interpolating it would have added a fourth place the same number is
 * rendered and a fourth chance for one of them to drift.
 *
 * NO FIXED BEST CASE PERCENTAGE MAY RETURN TO THIS FILE. After D-02 moved the
 * stored weight to 0.50 and D-03 made the percentage a per-viewer toggle, a
 * literal contradicts every figure on the page and contradicts it DIFFERENTLY
 * for every viewer. ForecastPage.test.tsx renders at Upside % 33 and again at 50
 * and asserts no Best Case percentage appears at either, so a literal that
 * happened to match one value still fails.
 */

/**
 * Narrow a period payload to the slice the workbook reads (D-14).
 *
 * `deals` is optional on the response but REQUIRED by the workbook, and the
 * absent case is a real one (a period with nothing open). Defaulting to an empty
 * list here produces an empty sheet rather than a thrown export — a missing
 * sheet is a worse answer than an empty one, because a reader cannot tell it
 * from a quarter that genuinely has no deals.
 */
function toWorkbookPeriod(r: ForecastResponse): ForecastWorkbookPeriod {
  return { period_label: r.period_label, arr: { deals: r.arr.deals ?? [] } };
}

/**
 * Read + validate ?period; default to the current quarter's label.
 *
 * quick-261001-m3v — the period is one of the five quarter labels in view
 * (Q1..Q4 of the current FY + Q1 of the next). The legacy CQ / NQ / FY keys
 * still resolve (deep links, the artifact footer's ?period=CQ): CQ and FY →
 * the current quarter, NQ → the next.
 */
function parsePeriod(raw: string | null, now: Date): string {
  const window = forecastQuarterWindow(now);
  if (raw && window.some((t) => t.label === raw)) return raw;
  if (raw === 'NQ') return nextForecastQuarterTab(now).label;
  return currentForecastQuarterTab(now).label;
}

/**
 * Map the reader's compact `freshness` ({ lastRefreshedAt, tier }) onto the
 * FreshnessPill `cached` variant (which wants ageSeconds + tier). The reader's
 * 'stale' tier is the same red band the pill renders; 'fresh'/'aging' map 1:1.
 */
function toFreshnessPill(
  freshness: { lastRefreshedAt: string; tier: 'fresh' | 'aging' | 'stale' } | undefined,
): FreshnessPillData {
  if (!freshness) {
    return { status: 'live', lastRefreshedAt: new Date().toISOString() };
  }
  const refreshedMs = Date.parse(freshness.lastRefreshedAt);
  const ageSeconds = Number.isNaN(refreshedMs)
    ? 0
    : Math.max(0, Math.floor((Date.now() - refreshedMs) / 1000));
  return {
    status: 'cached',
    lastRefreshedAt: freshness.lastRefreshedAt,
    ageSeconds,
    tier: freshness.tier,
  };
}

export function ForecastPage({ now = new Date() }: { now?: Date } = {}): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // quick-261001-m3v — `now` decides which five quarters are in view and
  // which is current; injectable so the tests are not calendar-dependent.
  const period = parsePeriod(searchParams.get('period'), now);

  /**
   * D-03 — THE ONE PIECE OF STATE THAT HOLDS THE VIEWER'S UPSIDE %.
   *
   * Component state, and deliberately nothing more: it is NOT a search param
   * beside `?period`, NOT localStorage/sessionStorage, NOT a persisted store,
   * and it is never sent to the server. Each of those would make the value
   * transmissible or durable, and D-03 makes it per-viewer and ephemeral — two
   * mounted pages are independent and a reload returns to the stored default.
   * A shareable URL in particular would let one viewer hand another a moved
   * forecast that looked like the house number.
   *
   * Every consumer receives it as a prop from here. Nothing below this line
   * sources `DEFAULT_UPSIDE_PCT` for itself.
   */
  // quick-261001-nym — TWO knobs: Product Best Case and Managed Services Best Case.
  const [upsidePct, setUpsidePct] = useState<{ product: number; managed: number }>({
    product: DEFAULT_UPSIDE_PCT,
    managed: DEFAULT_UPSIDE_PCT,
  });

  /**
   * T-05.3-08-05 — a GENERIC export-failure message, held as a boolean flag
   * rather than an error string. The underlying error can carry payload
   * contents, and an export failure is not worth putting revenue figures into
   * the DOM for. A failed export must also not blank the page: this renders
   * beside the button, it does not replace anything.
   */
  const [exportFailed, setExportFailed] = useState(false);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  // All three payloads in hand: D-14 exports both quarter sheets whichever
  // period is on screen, and D-07 renders the FY box on every period.
  const quarters = useForecastQuarters(now);
  const activeTab =
    quarters.window.find((t) => t.label === period) ?? currentForecastQuarterTab(now);
  const currentTab = currentForecastQuarterTab(now);
  const active = quarters.byLabel.get(activeTab.label) ?? quarters.current;
  const { data, isLoading, isError, error, refetch } = active;

  function handlePeriodChange(next: string): void {
    setSearchParams({ period: next });
  }

  // The section card's day-band label still thinks in CQ/NQ terms: the current
  // quarter is at its day-in-quarter; any other tab is not (closed or not started).
  const legacyPeriod: ForecastPeriod = activeTab.state === 'current' ? 'CQ' : 'NQ';

  // Clamped again on the way in, so the model is bounded even if a future caller
  // reaches this setter without going through the toggle (T-05.3-08-02).
  function handleUpsidePctChange(stream: 'product' | 'managed', next: number): void {
    setUpsidePct((u) => ({ ...u, [stream]: clampUpsidePct(next) }));
  }

  const freshness = toFreshnessPill(data?.freshness);

  // The strip and the section card read the SAME deals and the SAME single ARR
  // budget (D-05), so the two cannot disagree about what the period is worth.
  const arrDeals = data?.arr.deals ?? [];
  const arrTarget = data?.arr.target?.value ?? null;

  /**
   * D-16 — the access panel replaces the sections ONLY for an unauthenticated
   * response.
   *
   * A cold cache (503 CACHE_NOT_READY) is a different failure with a different
   * fix, and folding the two together would tell a signed-in exec staring at a
   * warming cache that they need to sign in. That path stays where it was:
   * threaded into the section card as isError/isLoading.
   */
  const isUnauthenticated = active.error instanceof UnauthenticatedError;

  // quick-260922-mvc — the Monday-morning archive list. Not fetched while the
  // viewer is signed out; the list itself renders nothing while loading or on
  // error, so it can never blank the tab.
  const workbooks = useForecastWorkbooks(!isUnauthenticated);

  // D-14 — both quarter sheets, whichever period is on screen.
  const cqData = quarters.current.data;
  const nqData = quarters.next.data;
  const canExport = cqData !== undefined && nqData !== undefined;

  /**
   * D-19 — THE LIVE ARR CALL PER QUARTER, FOR THE YEAR-TO-DATE BOX
   * (quick-260917-nfy).
   *
   * Built here, on every render, from the SAME weighting module the equation
   * strip reads, and keyed by the reader's `period_label` (the same
   * `FY{year}-Q{n}` the ytd payload labels its quarters with). Deliberately NOT
   * a server field and deliberately NOT cached: D-19 requires the year-to-date
   * total to move dollar-for-dollar with the current quarter's ARR call when a
   * viewer moves the Upside %, and anything baked at load would leave the box
   * disagreeing with the headline directly above it.
   *
   * Both quarter payloads are in hand whichever period is on screen, so the
   * box shows Q3 AND Q4 gross on every period — the CQ call is Q3's gross (the
   * one that enters the total) and the NQ call is Q4's (a column only). This
   * map is the ONLY source of Q3/Q4 gross; nothing else supplies a Q4 call.
   */
  // quick-261001-m3v — every quarter payload in view supplies its call, so an
  // open quarter's column is live on whichever tab is on screen; a banked
  // quarter ignores its entry (D-20).
  const arrCallByQuarter = new Map<string, number>();
  for (const tab of quarters.window) {
    const payload = quarters.byLabel.get(tab.label)?.data;
    if (payload !== undefined) {
      arrCallByQuarter.set(payload.period_label, streams(payload.arr.deals ?? [], upsidePct).combinedArr);
    }
  }

  /**
   * quick-260921-f73 — THE YEAR-TO-DATE BOX READS THROUGH THE QUARTER ON
   * SCREEN. On CQ that is the current fiscal quarter (the model's default, so
   * undefined); on NQ it is the next quarter's fiscal quarter, parsed from its
   * own period_label; on FY it is Q4. Before this the Q4 view still totalled
   * through Q3 (John, 2026-09-21).
   *
   * quick-261001-ibu — once the current quarter is Q4, the next quarter is Q1
   * of the NEXT fiscal year. The box still reads the current fiscal year (its
   * quarters are the CQ payload's FY), so on that view it reads through Q4 —
   * the full year — rather than parsing "Q1" and collapsing to the current
   * year's Q1 (John, 2026-10-01: "flip ... forward into q4, with q1 as the
   * next tab. keep all else the same").
   */
  // quick-261001-m3v — five tabs: a quarter of the current fiscal year reads
  // the year THROUGH THAT QUARTER (a closed Q2 tab reads "At end of Q2"); the
  // Q1 tab of the NEXT fiscal year reads the full current year (through Q4).
  const ytdAsOfQuarter: number | undefined =
    activeTab.fiscal_year !== currentTab.fiscal_year ? 4 : activeTab.fiscal_quarter;

  function handleDownload(): void {
    if (cqData === undefined || nqData === undefined) return;
    setExportFailed(false);
    // The Upside % goes into the workbook as an ARGUMENT, not over the wire:
    // generation is client-side precisely so this per-viewer lens never becomes
    // a request param (D-03, T-05.3-06-03).
    downloadWorkbook(toWorkbookPeriod(cqData), toWorkbookPeriod(nqData), upsidePct).catch(() => {
      setExportFailed(true);
    });
  }

  return (
    <AppShell
      brandSuffix="Forecast"
      crossLink={{ label: 'Main scorecard ↗', to: '/main' }}
      extraLinks={[{ label: '2027 Plan ↗', to: '/2027-plan' }]}
      freshnessSlot={<FreshnessPill freshness={freshness} />}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ForecastPeriodSelector activePeriod={activeTab.label} onChange={handlePeriodChange} now={now} />
          {!isUnauthenticated && (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={handleDownload}
                disabled={!canExport}
                data-testid="forecast-download"
                className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download .xlsx
              </button>
              <ForecastWorkbookArchive workbooks={workbooks.data?.workbooks} />
              {exportFailed && (
                <span className="text-[0.7rem] text-bad-text" data-testid="forecast-download-error">
                  Export failed. Try again.
                </span>
              )}
            </div>
          )}
        </div>

        {/* quick-261001-m3v — a closed quarter is history: Salesforce Closed Won
            as the quarter ended. The year-to-date box below still banks the
            finance-workbook figure for it (D-20), which can differ. */}
        {!isUnauthenticated && activeTab.state === 'closed' && (
          <p className="text-xs text-ink-500" data-testid="forecast-closed-note">
            {activeTab.title} is closed. Figures are Salesforce Closed Won at quarter end; the
            year-to-date box banks the finance figure.
          </p>
        )}

        {isUnauthenticated && <ForecastAccessPanel signInUrl={resolveLoginUrl('/api/forecast')} />}

        {!isUnauthenticated && (
          <>
        {/* D-13c — the summary strip, read as an equation, above the section
            card. The Upside % control goes into the strip's own summary row
            (D-13a) rather than onto a line of its own, but the PAGE still owns
            the value: the strip takes the control as an opaque slot and never
            sees the setter. */}
        {/* quick-261001-nym — the Upside knobs moved OFF the strip into the
            Product and Managed Services cards (John, 2026-10-01). */}
        <ForecastEquationStrip deals={arrDeals} upsidePct={upsidePct} target={arrTarget} />

        <div className="space-y-6" data-testid="forecast-sections">
          {/* The one revenue section (D-10).
              `upsidePct` — THE PAGE IS THE SOLE SUPPLIER OF THIS VALUE, and it
              is now the live toggle state rather than the stored default. The
              section card receives it and forwards it to the waterfall and the
              deal table, so the footer moves with the knob rather than sitting
              frozen beside a headline that moved. */}
          <ForecastSectionCard
            sectionKey="arr"
            period={legacyPeriod}
            upsidePct={upsidePct}
            onUpsideChange={handleUpsidePctChange}
            upsideDisabled={activeTab.state === 'closed'}
            section={
              data?.arr ?? {
                key: 'ARR',
                committed: { tier: 'committed', deals_count: 0, total: 0 },
                upside: { tier: 'upside', deals_count: 0, total: 0 },
                closed_won_total: 0,
                weighted_total: 0,
                breakdown: [],
                target: null,
                verdict: 'no_target',
              }
            }
            isLoading={isLoading}
            isError={isError}
            error={error}
            refetch={() => {
              void refetch();
            }}
          />
        </div>

        {/* D-19 — THE FOOT OF THE TAB, BELOW MANAGED SERVICES. Not the top:
            the box exists to put the quarter's headline in the context of the
            year, and it is the last thing read, after the sections it
            qualifies. D-07 renders it on all three periods — the reader carries
            `ytd` on every one.

            quick-260917-nfy — ONE COLUMN PER QUARTER Q1..Q4 ON EVERY PERIOD,
            CQ included. The reader's ytd scope is Q1..fq on CQ but Q1..Q4 on NQ
            and FY, so the three payloads already in hand are merged by label —
            the active period's list first, so its own quarter objects win.

            quick-260918-fai — GROSS ONLY (artifact v29). Every column reads
            gross sales against the quarter's gross ARR budget; the total is
            Q1 + Q2 gross plus the current quarter's GROSS call, 'At end of
            Q3', and Q4 never enters it. No churn, no net. */}
        {data?.ytd !== undefined && (
          <ForecastYtdBox
            quarters={mergeYtdQuarters([
              data.ytd.quarters,
              ...quarters.window.map((t) => quarters.byLabel.get(t.label)?.data?.ytd.quarters),
            ])}
            arrByQuarterLabel={arrCallByQuarter}
            asOfQuarter={ytdAsOfQuarter}
          />
        )}

        {/* quick-261001-nym — Exec Scratchpad, at the foot of every tab. */}
        <ForecastScratchpad deals={arrDeals} quarterTitle={activeTab.title} />
          </>
        )}
      </div>
    </AppShell>
  );
}
